import React from 'react';
import {
  Box,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { RefreshIconButton } from '../RefreshIconButton';
import {
  INVESTIGATE_PAGE_SUBTITLE,
  RISK_PATTERN_EVIDENCE_REVIEW_HEADING,
} from '../../domain/platform/platformOrientationModel';
import { PlatformWorkflowStrip } from '../platform/PlatformWorkflowStrip';
import { buyerConditionDisplayName } from '../../domain/buyerFacing/conditionDisplayNames';

export interface IncidentHeaderProps {
  activeScope: { orgId?: string; siteId?: string; source?: string };
  setScopeDialogOpen: (open: boolean) => void;
  incidentsAdvancedEligible: boolean;
  incidentsUiMode: 'operator' | 'advanced';
  setIncidentsUiModePersist: (mode: 'operator' | 'advanced') => void;
  onRefresh: () => void;
  loading: boolean;
  nextStep?: {
    title: string;
    detail: string;
    label: string;
    onClick: () => void;
  } | null;
  problemContext?: { place: string; problem: string } | null;
}

const IncidentHeader: React.FC<IncidentHeaderProps> = ({
  activeScope,
  setScopeDialogOpen,
  incidentsAdvancedEligible,
  incidentsUiMode,
  setIncidentsUiModePersist,
  onRefresh,
  loading,
  nextStep = null,
  problemContext = null,
}) => {
  const scopeLabel = `Scope: ${String(activeScope.orgId || '').trim() || '?'} / ${String(activeScope.siteId || '').trim() || '?'}`;
  const scopeColor =
    !String(activeScope.orgId || '').trim() ||
    !String(activeScope.siteId || '').trim() ||
    (String(activeScope.orgId || '').trim() === 'default' && String(activeScope.siteId || '').trim() === 'default')
      ? 'warning'
      : 'success';
  const showEscalationBanner = incidentsUiMode !== 'operator' && nextStep?.label === 'Open escalations';

  return (
    <Box sx={{ mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box sx={{ minWidth: 280, flex: 1 }}>
          <Typography sx={{ fontSize: { xs: 28, md: 30 }, fontWeight: 700, lineHeight: 1.05 }}>
            {RISK_PATTERN_EVIDENCE_REVIEW_HEADING}
          </Typography>
          {problemContext ? (
            <Box data-testid="incident-workflow-problem-context" sx={{ mt: 0.75 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                {problemContext.place}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {buyerConditionDisplayName(problemContext.problem)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {INVESTIGATE_PAGE_SUBTITLE}
            </Typography>
          )}
          <Box sx={{ mt: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Tooltip
              title={
                activeScope.source === 'jwt'
                  ? 'Scope is derived from your JWT. Click to review or adjust it.'
                  : 'Click to adjust the org/site scope used for this queue.'
              }
            >
              <Chip
                size="small"
                clickable
                color={scopeColor}
                onClick={() => setScopeDialogOpen(true)}
                label={scopeLabel}
                variant="outlined"
              />
            </Tooltip>
            {incidentsAdvancedEligible && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={incidentsUiMode}
                onChange={(_, value) => {
                  if (value === 'operator' || value === 'advanced') {
                    setIncidentsUiModePersist(value);
                  }
                }}
              >
                <ToggleButton value="operator">Operator</ToggleButton>
                <ToggleButton value="advanced">Advanced</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
          {showEscalationBanner && nextStep ? (
            <Box
              sx={{
                mt: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Escalations waiting
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={nextStep.onClick}
                sx={{ fontWeight: 800, boxShadow: 'none' }}
              >
                {nextStep.label}
              </Button>
            </Box>
          ) : null}
        </Box>

        <RefreshIconButton onClick={onRefresh} loading={loading} title="Refresh pattern review" />
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <PlatformWorkflowStrip
          currentStep="review"
          compact
          framed={false}
          labelOverrides={{ scan: 'Safety Scan' }}
        />
      </Box>
    </Box>
  );
};

export default IncidentHeader;
